import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Sidebar } from '../../shared/sidebar/sidebar';
import { UserService } from '../../core/services/user.service';
import { Trainee } from '../../core/models/user.model';

@Component({
  selector: 'app-trainees',
  imports: [CommonModule, FormsModule, Sidebar],
  templateUrl: './trainees.html',
  styleUrl: './trainees.scss',
})
export class Trainees implements OnInit {
  searchQuery: string = '';
  currentPage: number = 1;
  itemsPerPage: number = 25;
  totalPages: number = 1;
  isLoading: boolean = false;

  trainees: Trainee[] = [];
  filteredTrainees: Trainee[] = [];

  constructor(private userService: UserService) {}

  ngOnInit() {
    this.loadTrainees();
  }

  loadTrainees() {
    this.isLoading = true;
    this.userService.getTrainees().subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success) {
          this.trainees = response.data;
          this.filteredTrainees = this.trainees;
          this.calculatePagination();
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading trainees:', error);
      }
    });
  }

  calculatePagination() {
    this.totalPages = Math.ceil(this.filteredTrainees.length / this.itemsPerPage);
  }

  get paginatedTrainees() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.filteredTrainees.slice(start, end);
  }

  onSearch() {
    if (!this.searchQuery.trim()) {
      this.filteredTrainees = this.trainees;
    } else {
      const q = this.searchQuery.toLowerCase();
      this.filteredTrainees = this.trainees.filter(t =>
        t.name.toLowerCase().includes(q) ||
        (t.email && t.email.toLowerCase().includes(q)) ||
        t.phoneNumber.toLowerCase().includes(q) ||
        (t.parentName && t.parentName.toLowerCase().includes(q))
      );
    }
    this.currentPage = 1;
    this.calculatePagination();
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  getPageNumbers(): (number | string)[] {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (this.totalPages <= maxVisible + 2) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      let start = Math.max(2, this.currentPage - 1);
      let end = Math.min(this.totalPages - 1, this.currentPage + 1);

      if (this.currentPage <= 3) {
        start = 2;
        end = 4;
      }

      if (this.currentPage >= this.totalPages - 2) {
        start = this.totalPages - 3;
        end = this.totalPages - 1;
      }

      if (start > 2) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < this.totalPages - 1) {
        pages.push('...');
      }

      pages.push(this.totalPages);
    }

    return pages;
  }

  getAvatar(name: string): string {
    const colors = ['0B8DD5', 'FF8B3D', '22C55E', 'F59E0B', 'EF4444', '8B5CF6', '06B6D4'];
    const colorIndex = name.length % colors.length;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${colors[colorIndex]}&color=fff`;
  }
}
